import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Bot, FileText, Zap, AlertTriangle, CheckCircle, Clock, Play, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAIAgents } from '@/hooks/useAIAgents';
import { useBills } from '@/hooks/useBills';
import { format } from 'date-fns';

interface BillTrigger {
  id: string;
  name: string;
  agent_id: string;
  trigger_conditions: {
    confidence_threshold?: number;
    amount_threshold?: number;
    bill_type?: string;
    processing_status?: string;
  };
  enabled: boolean;
  created_at: string;
  execution_count: number;
}

export const AgentBillIntegration = () => {
  const { toast } = useToast();
  const { agents, executeAgent, isExecuting } = useAIAgents();
  const { bills } = useBills();
  const [triggers, setTriggers] = useState<BillTrigger[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [triggerType, setTriggerType] = useState<string>('confidence');
  const [isCreatingTrigger, setIsCreatingTrigger] = useState(false);

  // Sample bill triggers
  useEffect(() => {
    const sampleTriggers: BillTrigger[] = [
      {
        id: '1',
        name: 'High Confidence Validation',
        agent_id: 'agent_1',
        trigger_conditions: {
          confidence_threshold: 95,
          processing_status: 'completed'
        },
        enabled: true,
        created_at: '2024-01-15T10:00:00Z',
        execution_count: 23
      },
      {
        id: '2',
        name: 'Large Amount Review',
        agent_id: 'agent_2',
        trigger_conditions: {
          amount_threshold: 1000,
          processing_status: 'completed'
        },
        enabled: true,
        created_at: '2024-01-12T14:30:00Z',
        execution_count: 8
      },
      {
        id: '3',
        name: 'Utility Bill Anomaly Check',
        agent_id: 'agent_3',
        trigger_conditions: {
          bill_type: 'utility',
          confidence_threshold: 80
        },
        enabled: false,
        created_at: '2024-01-10T09:15:00Z',
        execution_count: 15
      }
    ];
    setTriggers(sampleTriggers);
  }, []);

  const handleTestTrigger = async (triggerId: string) => {
    const trigger = triggers.find(t => t.id === triggerId);
    if (!trigger) return;

    // Find a matching bill for testing
    const testBill = bills?.find(bill => {
      const conditions = trigger.trigger_conditions;
      return (
        (!conditions.confidence_threshold || (bill.ai_confidence && bill.ai_confidence >= conditions.confidence_threshold)) &&
        (!conditions.amount_threshold || bill.amount >= conditions.amount_threshold) &&
        (!conditions.bill_type || bill.bill_type === conditions.bill_type) &&
        (!conditions.processing_status || bill.processing_status === conditions.processing_status)
      );
    });

    if (testBill && trigger.agent_id) {
      executeAgent({
        agentId: trigger.agent_id,
        inputData: {
          bill_id: testBill.id,
          bill_data: testBill,
          trigger_type: 'bill_processing',
          trigger_conditions: trigger.trigger_conditions
        }
      });

      toast({
        title: "Agent execution started",
        description: `Testing trigger "${trigger.name}" with bill ${testBill.id.substring(0, 8)}...`,
      });
    } else {
      toast({
        title: "No matching bills",
        description: "No bills found that match the trigger conditions.",
        variant: "destructive",
      });
    }
  };

  const toggleTrigger = (triggerId: string, enabled: boolean) => {
    setTriggers(prev => prev.map(trigger => 
      trigger.id === triggerId ? { ...trigger, enabled } : trigger
    ));

    toast({
      title: enabled ? "Trigger enabled" : "Trigger disabled",
      description: `Bill trigger has been ${enabled ? 'enabled' : 'disabled'}.`,
    });
  };

  const getConditionsSummary = (conditions: any) => {
    const parts = [];
    if (conditions.confidence_threshold) {
      parts.push(`Confidence ≥ ${conditions.confidence_threshold}%`);
    }
    if (conditions.amount_threshold) {
      parts.push(`Amount ≥ $${conditions.amount_threshold}`);
    }
    if (conditions.bill_type) {
      parts.push(`Type: ${conditions.bill_type}`);
    }
    if (conditions.processing_status) {
      parts.push(`Status: ${conditions.processing_status}`);
    }
    return parts.join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold">Bill Processing Integration</h3>
          <p className="text-sm text-muted-foreground">
            Configure AI agents to automatically process bills based on conditions
          </p>
        </div>
        <Button onClick={() => setIsCreatingTrigger(true)}>
          <Zap className="h-4 w-4 mr-2" />
          Create Trigger
        </Button>
      </div>

      {/* Integration Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Triggers</CardTitle>
            <Bot className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {triggers.filter(t => t.enabled).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {triggers.length} total triggers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
            <Zap className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {triggers.reduce((sum, t) => sum + t.execution_count, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Automated executions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing Bills</CardTitle>
            <FileText className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {bills?.filter(b => b.processing_status === 'processing').length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently processing
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bill Triggers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Bill Processing Triggers</CardTitle>
          <CardDescription>
            Automated triggers that execute agents based on bill processing events
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger Name</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Executions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {triggers.map((trigger) => {
                const agent = agents?.find(a => a.id === trigger.agent_id);
                return (
                  <TableRow key={trigger.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{trigger.name}</span>
                        <span className="text-xs text-muted-foreground">
                          Created {format(new Date(trigger.created_at), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {agent ? (
                        <div className="flex items-center space-x-2">
                          <Bot className="h-4 w-4" />
                          <span className="text-sm">{agent.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No agent</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs">
                        {getConditionsSummary(trigger.trigger_conditions)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={trigger.enabled}
                          onCheckedChange={(checked) => toggleTrigger(trigger.id, checked)}
                        />
                        <Badge variant={trigger.enabled ? 'default' : 'outline'}>
                          {trigger.enabled ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{trigger.execution_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleTestTrigger(trigger.id)}
                          disabled={isExecuting || !trigger.enabled}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Real-time Processing Monitor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Real-time Processing Monitor</span>
          </CardTitle>
          <CardDescription>
            Live view of bills being processed by AI agents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {bills?.filter(bill => bill.processing_status === 'processing').slice(0, 5).map((bill) => (
              <div key={bill.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div>
                    <p className="text-sm font-medium">
                      {bill.file_name || `Bill ${bill.id.substring(0, 8)}...`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Processing • ${bill.amount} • {bill.bill_type}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">Processing</Badge>
                  <Clock className="h-4 w-4 text-blue-500 animate-spin" />
                </div>
              </div>
            )) || (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No bills currently being processed</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Trigger Modal */}
      {isCreatingTrigger && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Create Bill Processing Trigger</CardTitle>
              <CardDescription>
                Set up automatic agent execution based on bill processing events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Trigger Name</label>
                  <input
                    className="w-full p-2 border rounded-md"
                    placeholder="Enter trigger name..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">AI Agent</label>
                  <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {agents?.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Trigger Condition</label>
                <Select value={triggerType} onValueChange={setTriggerType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confidence">Confidence Threshold</SelectItem>
                    <SelectItem value="amount">Amount Threshold</SelectItem>
                    <SelectItem value="type">Bill Type</SelectItem>
                    <SelectItem value="status">Processing Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreatingTrigger(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsCreatingTrigger(false)}>
                  Create Trigger
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};